import React from 'react';
import { definePage, defineResource } from '@restavo/crud';
import TransactionCategoryForm from 'src/components/Accounting/TransactionCategoryForm';
import EmptyState from 'src/components/Base/EmptyState';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  tenantTransactionCategories,
  tenantTransactionCategoryCreate,
  tenantTransactionCategoryDelete,
  tenantTransactionCategoryUpdate,
} from 'src/services/apis/tenantRequests';
import { TransactionCategoryStatusEnum, TransactionCategoryTypeEnum } from 'src/utils/accounting';
import { QUERIES } from 'src/utils/react-query';
import { splitTags } from 'src/utils/tags';

const transactionCategories = defineResource({
  key: QUERIES.TRANSACTION_CATEGORIES,
  // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured take)
  // returned every row in one shot; the crud layer's paginated query needs a concrete page
  // size, and 1000 is the practical match — the same reach the pilot used for an
  // effectively-unbounded list.
  pageSize: 1000,
  api: {
    list: tenantTransactionCategories,
    create: tenantTransactionCategoryCreate,
    update: tenantTransactionCategoryUpdate,
    remove: tenantTransactionCategoryDelete,
  },
  // The hand-written page filtered name/description in memory over the fetched set.
  search: 'client',
  searchFields: ['name', 'description'],
  // Both plain server enum columns; same labels as the columns' tag displays below.
  filterable: [
    {
      field: 'type',
      labelKey: 'common.type',
      type: 'enum',
      options: [
        { id: String(TransactionCategoryTypeEnum.INCOME), labelKey: 'transactionCategoryTypes.income' },
        { id: String(TransactionCategoryTypeEnum.EXPENSE), labelKey: 'transactionCategoryTypes.expense' },
        { id: String(TransactionCategoryTypeEnum.INVESTMENT), labelKey: 'transactionCategoryTypes.investment' },
        { id: String(TransactionCategoryTypeEnum.OTHER), labelKey: 'transactionCategoryTypes.other' },
      ],
    },
    {
      field: 'status',
      labelKey: 'common.status',
      type: 'enum',
      options: [
        { id: String(TransactionCategoryStatusEnum.ACTIVE), labelKey: 'transactionCategoryStatuses.active' },
        { id: String(TransactionCategoryStatusEnum.ARCHIVED), labelKey: 'transactionCategoryStatuses.archived' },
      ],
    },
  ],
});

export const transactionCategoriesPage = definePage(transactionCategories, {
  titleKey: 'titles.transactionCategoriesPage',
  descriptionKey: 'common.transactionCategories',
  createLabelKey: 'actions.createNewTransactionCategory',
  updateLabelKey: 'actions.updateTransactionCategory',
  deleteHeadingKey: 'messages.areYouSureToDeleteThisTransactionCategory',
  columns: [
    // The column a user edits by name must survive every prefs reconciliation.
    { key: 'name', headerKey: 'common.name', sortable: true, alwaysVisible: true },
    { key: 'description', headerKey: 'common.description' },
    {
      key: 'type',
      headerKey: 'common.type',
      // Same labels and Carbon types TransactionCategoryTypeTag used, as a display kind.
      display: {
        kind: 'tag',
        map: {
          [TransactionCategoryTypeEnum.INCOME]: { labelKey: 'transactionCategoryTypes.income', type: 'green' },
          [TransactionCategoryTypeEnum.EXPENSE]: { labelKey: 'transactionCategoryTypes.expense', type: 'red' },
          [TransactionCategoryTypeEnum.INVESTMENT]: {
            labelKey: 'transactionCategoryTypes.investment',
            type: 'warm-gray',
          },
          [TransactionCategoryTypeEnum.OTHER]: { labelKey: 'transactionCategoryTypes.other', type: 'gray' },
        },
      },
    },
    {
      key: 'status',
      headerKey: 'common.status',
      sortable: true,
      // Same labels and Carbon types TransactionCategoryStatusTag used, as a display kind.
      display: {
        kind: 'tag',
        map: {
          [TransactionCategoryStatusEnum.ACTIVE]: {
            labelKey: 'transactionCategoryStatuses.active',
            type: 'cyan',
          },
          [TransactionCategoryStatusEnum.ARCHIVED]: {
            labelKey: 'transactionCategoryStatuses.archived',
            type: 'gray',
          },
        },
      },
    },
    // server sorts createdAt ASC, so a freshly saved category lands at the bottom; a visible
    // updatedAt would not make that clearer. Hidden by default, one toggle away.
    { key: 'updatedAt', headerKey: 'common.updated', display: { kind: 'date' }, defaultHidden: true, sortable: true },
  ],
  form: TransactionCategoryForm,
  emptyState: <EmptyState />,
  toPayload: (values: any, params) => ({
    ...values,
    restaurantId: params.restaurantId,
    tags: splitTags(values.tags),
  }),
  useTitleVars: () => ({ restaurantName: useCurrentRestaurant()?.name }),
});
